// This file configures the initialization of Sentry on the client.
// The added config here will be used whenever a users loads a page in their browser.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

;globalThis["_sentryRouteManifest"] = "{\"dynamicRoutes\":[],\"staticRoutes\":[],\"isrRoutes\":[]}";globalThis["_sentryNextJsVersion"] = "16.1.6";import * as Sentry from "@sentry/nextjs";

Sentry.init({
  dsn: "https://a34598051a5a13075a2ad186b79bb810@o553113.ingest.us.sentry.io/4508637282566144",

  // CRITICAL: Set the release to match uploaded source maps
  release: process.env.NEXT_PUBLIC_SENTRY_RELEASE,

  // Add optional integrations for additional features
  integrations: [Sentry.replayIntegration()],

  // Define how likely traces are sampled. Adjust this value in production, or use tracesSampler for greater control.
  tracesSampleRate: 0.1,

  // Define how likely Replay events are sampled.
  // This sets the sample rate to be 10%. You may want this to be 100% while
  // in development and sample at a lower rate in production
  replaysSessionSampleRate: 0.1,

  // Define how likely Replay events are sampled when an error occurs.
  replaysOnErrorSampleRate: 1.0,

  // Setting this option to true will print useful information to the console while you're setting up Sentry.
  debug: false,

  enabled: process.env.NODE_ENV === "production",

  // Filter out intentional user-triggered events that aren't actual errors
  beforeSend(event, hint) {
    const error = hint.originalException;

    // Filter out route cancellation - this is intentional user behavior, not an error
    if (
      error &&
      typeof error === "object" &&
      "name" in error &&
      error.name === "RouteChangeCancelled"
    ) {
      return null;
    }

    // Filter out unauthorized ApiErrors - these are expected when user session expires
    if (
      error &&
      typeof error === "object" &&
      "name" in error &&
      error.name === "ApiError" &&
      "status" in error &&
      error.status === 401
    ) {
      return null;
    }

    // Also filter by message for backwards compatibility with old string throws
    if (
      event.exception?.values?.[0]?.value?.includes(
        "Route change cancelled by user",
      ) ||
      event.exception?.values?.[0]?.value?.includes("Route change aborted") ||
      event.exception?.values?.[0]?.value?.includes("Not authorized")
    ) {
      return null;
    }

    // Filter out PostHog session recorder errors - known rrweb bug where
    // the recorder tries to access childNodes on a DOM node React already removed
    const frames = event.exception?.values?.[0]?.stacktrace?.frames;
    if (
      frames?.some(
        (frame) =>
          frame.filename?.includes("posthog-recorder") ||
          frame.function === "removeNodeFromMap",
      )
    ) {
      return null;
    }

    // Telegram in-app browser injects a Mini App bridge that calls postEvent
    // with methods not supported on every client version; thrown from third-party
    // script we don't control.
    if (
      event.exception?.values?.[0]?.value?.includes("Error invoking postEvent")
    ) {
      return null;
    }

    // Filter out VGS Collect iframe load timeouts (FLUID-CHECKOUT-Q).
    // VGS's library has its own internal 20s timer that rejects as
    // "Iframe didn't loaded in 20000 ms" whenever its iframe host div
    // disappears before mount — most commonly when the user switches
    // payment methods before iframes finish loading. form.unmount() does
    // not cancel that internal promise, so the rejection still fires.
    // Our own 25s timer + fallback UI already covers the real ad-blocker
    // case, so these reports are pure noise.
    const errorValue = event.exception?.values?.[0]?.value || "";
    if (
      errorValue.includes("Iframe didn't loaded") &&
      frames?.some((frame) => frame.filename?.includes("vgs-collect"))
    ) {
      return null;
    }

    return event;
  },
});

// Swallow VGS Collect iframe timeout rejections so they don't spam the
// browser console. Matches the same error filtered above in beforeSend.
if (typeof window !== "undefined") {
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason as unknown;
    const message =
      typeof reason === "string"
        ? reason
        : reason instanceof Error
          ? reason.message
          : typeof (reason as { message?: unknown })?.message === "string"
            ? (reason as { message: string }).message
            : "";
    if (message.includes("Iframe didn't loaded")) {
      event.preventDefault();
    }
  });
}

export const onRouterTransitionStart = Sentry.captureRouterTransitionStart;
